import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import type { AnswerOption } from '@/lib/api/types';
import { touchTargetMinimum } from '@/theme/tokens';

interface AnswerOptionListProps {
  options: AnswerOption[];
  selected: number | string | null;
  onSelect: (value: number | string) => void;
  name: string;
}

/**
 * The answer options. Whole rows, not radio buttons (specification 8.4): a 44 px target is the
 * minimum that can be hit reliably while standing at a stand and talking.
 *
 * Built as a radiogroup so the whole questionnaire can be completed with the keyboard alone,
 * which is task B3's acceptance criterion.
 */
export function AnswerOptionList({ options, selected, onSelect, name }: AnswerOptionListProps) {
  return (
    <Box role="radiogroup" aria-label={name} sx={{ display: 'grid', gap: 1.25 }}>
      {options.map((option) => {
        const isSelected = selected !== null && String(selected) === String(option.value);

        // Most question types have no label and fall back to a single digit. The eight
        // sovereignty objectives label theirs "SEAL-0" … "SEAL-4", which is six characters — a
        // fixed square badge wraps them onto two lines and looks broken.
        const badge = String(option.label ?? option.value);
        const isWide = badge.length > 2;

        return (
          <ButtonBase
            key={String(option.value)}
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(option.value)}
            sx={{
              minHeight: touchTargetMinimum + 12,
              px: 2,
              py: 1.5,
              gap: 2,
              width: '100%',
              justifyContent: 'flex-start',
              textAlign: 'left',
              borderRadius: 1.5,
              border: 2,
              borderColor: isSelected ? 'primary.main' : 'divider',
              backgroundColor: isSelected ? 'action.selected' : 'transparent',
              transition: 'border-color 120ms, background-color 120ms',
              '&:hover': { borderColor: isSelected ? 'primary.main' : 'text.disabled' },
            }}
          >
            <Box
              aria-hidden
              sx={{
                flexShrink: 0,
                // Square for a bare digit, wide enough for "SEAL-0" — and never wrapping.
                minWidth: 30,
                height: 30,
                px: isWide ? 1 : 0,
                borderRadius: 1,
                display: 'grid',
                placeItems: 'center',
                whiteSpace: 'nowrap',
                fontSize: isWide ? '0.6875rem' : '0.9375rem',
                fontWeight: 680,
                letterSpacing: isWide ? '0.02em' : 0,
                fontVariantNumeric: 'tabular-nums',
                border: 2,
                borderColor: isSelected ? 'primary.main' : 'divider',
                color: isSelected ? 'primary.main' : 'text.secondary',
              }}
            >
              {badge}
            </Box>

            <Typography
              variant="body1"
              // The Danish option texts run long. Without this the row clips them instead of
              // wrapping, because the badge above is the only thing allowed not to shrink.
              sx={{ fontWeight: isSelected ? 560 : 400, minWidth: 0, whiteSpace: 'normal' }}
            >
              {option.text}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
